export const useLocation = () => {
  const navigator = isClient() ? window.navigator : undefined;
  const isGeolocationSupported = ref(navigator && "geolocation" in navigator);
  const locationPermissionStatus = useSafeLocalStorage("location-status", "unknown");
  const isLocationSupported = computed(
    () => !isClient() || (isGeolocationSupported.value && locationPermissionStatus.value !== "denied")
  );
  const loading = ref(!isClient());
  const coords = ref({});
  const error = ref(null);
  const locatedAt = ref(null);
  const watcher = ref(null);
  const geolocationSettings = {
    enableHighAccuracy: true,
    maximumAge: 30000,
    timeout: 5000,
  };
  const useWeb = ref(false);

  async function updatePosition(position) {
    locatedAt.value = position.timestamp;
    coords.value = {
      latitude: position.coords.latitude,
      longitude: position.coords.longitude,
      altitude: position.coords.altitude,
      accuracy: position.coords.accuracy,
      altitudeAccuracy: position.coords.altitudeAccuracy,
      heading: position.coords.heading,
      speed: position.coords.speed,
    };
    error.value = null;
    locationPermissionStatus.value = "allowed";
    if (!isNumber(position.coords.altitude)) {
      useWeb.value = true;
    }
    if (!useWeb.value) {
      loading.value = false;
    }
  }

  function handleError(err) {
    error.value = err;
    if (err.code === 1) {
      locationPermissionStatus.value = "denied";
    }
  }

  async function updateLocation() {
    if (!isGeolocationSupported.value) {
      return;
    }
    loading.value = true;
    locationPermissionStatus.value = "unknown";
    return new Promise((resolve) => {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          updatePosition(position);
          resolve();
        },
        (err) => {
          handleError(err);
          resolve();
        },
        geolocationSettings
      );
    });
  }

  function startWatching() {
    if (!isGeolocationSupported.value) {
      return;
    }
    loading.value = true;
    locationPermissionStatus.value = "unknown";
    watcher.value = navigator.geolocation.watchPosition(updatePosition, handleError, geolocationSettings);
  }

  function stopWatching() {
    if (watcher.value && navigator) {
      navigator.geolocation.clearWatch(watcher.value);
    }
  }

  const isWatching = computed(() => {
    return watcher.value !== null;
  });

  watch([coords, useWeb], async () => {
    if (useWeb.value && isNumber(coords.value?.latitude) && !coords.value?.webAltitude) {
      try {
        const { data } = await useFetchElevation(coords.value);
        coords.value = {
          ...coords.value,
          webAltitude: data.value,
        };
      } catch (error) {
        error.value = error;
      }
      loading.value = false;
    }
  });

  const location = computed(() => {
    if (useWeb.value) {
      return {
        ...coords.value,
        altitude: coords.value?.webAltitude,
        altitudeAccuracy: null,
        altitudeSource: "web",
      };
    }
    return coords.value;
  });

  return {
    updateLocation,
    startWatching,
    stopWatching,
    isWatching,
    useWeb,
    locationPermissionStatus,
    isLocationSupported,
    location,
    locatedAt,
    error,
    loading,
  };
};
